import React from 'react';
import { Grid, GridItem } from '@patternfly/react-core';
import { useWallet } from '../../../hooks/useWallet';
import { useRenewalAutopay } from '../../../hooks/useRenewalAutopay';
import { useInvoiceAutopay } from '../../../hooks/useInvoiceAutopay';
import WalletCard from './WalletCard';
import SavedCardsCard from './SavedCardsCard';
import RenewalAutopayCard from './RenewalAutopayCard';
import InvoiceAutopayCard from './InvoiceAutopayCard';

/**
 * "Prepaid balance & cards" tab of Organization › Billing: the wallet card and the saved-cards
 * card side by side, and below them the two automatic payment settings that spend from both —
 * domain renewals, then all invoices. They are saved separately, but the second also pays renewal
 * invoices, so the renewal card is told whether it is on (`null` while it is unavailable or not
 * loaded).
 * One `useWallet()` fetch is shared by the wallet card and its Top-up modal; a card change
 * (default / removal) refetches it so the "default card" shown for top-ups stays right, and
 * refetches both autopay settings, whose "no saved card" warnings depend on it.
 * Mounted lazily by the parent's `<Tabs mountOnEnter>`, so nothing is fetched until the tab opens.
 */
const WalletTab: React.FunctionComponent = () => {
  const walletState = useWallet();
  const autopayState = useRenewalAutopay();
  const invoiceAutopayState = useInvoiceAutopay();

  return (
    <Grid hasGutter md={6}>
      <GridItem>
        <WalletCard wallet={walletState} />
      </GridItem>
      <GridItem>
        <SavedCardsCard
          onChanged={() => {
            void walletState.refetch();
            void autopayState.refetch();
            void invoiceAutopayState.refetch();
          }}
        />
      </GridItem>
      {/* Hidden until the backend exposes the route (it answers 404 before then). */}
      {autopayState.available && (
        <GridItem span={12}>
          <RenewalAutopayCard
            autopayState={autopayState}
            invoiceAutopayEnabled={
              invoiceAutopayState.available ? (invoiceAutopayState.autopay?.enabled ?? null) : null
            }
          />
        </GridItem>
      )}
      {invoiceAutopayState.available && (
        <GridItem span={12}>
          <InvoiceAutopayCard autopayState={invoiceAutopayState} />
        </GridItem>
      )}
    </Grid>
  );
};

export default WalletTab;
